/**
 * LaTeX Renderer — KaTeX integration
 * Per PLAN.md latex/Renderer.js
 */

import katex from 'katex';
import { parseLatex } from './Parser.js';

// Render cache for performance
const renderCache = new Map();
const MAX_CACHE_SIZE = 500;

/**
 * Render LaTeX content to HTML
 * @param {string} input - User input (shorthand or LaTeX)
 * @param {boolean} displayMode - Display mode (larger, centered)
 * @returns {string} HTML string
 */
export function renderLatex(input, displayMode = true) {
    if (!input || input.trim() === '') {
        return '<span class="latex-empty">Empty</span>';
    }

    // Check cache
    const cacheKey = `${input}:${displayMode}`;
    if (renderCache.has(cacheKey)) {
        return renderCache.get(cacheKey);
    }

    try {
        // Parse shorthand to LaTeX
        const latex = parseLatex(input);

        // Render with KaTeX - always use display mode for node rendering
        const html = katex.renderToString(latex, {
            displayMode: true,  // Always use display mode for larger equations
            throwOnError: false,
            errorColor: '#cc0000',
            trust: false,
            strict: false,
            output: 'html'
        });

        // Cache result
        if (renderCache.size >= MAX_CACHE_SIZE) {
            // Remove oldest entries
            const keysToDelete = Array.from(renderCache.keys()).slice(0, 100);
            keysToDelete.forEach(k => renderCache.delete(k));
        }
        renderCache.set(cacheKey, html);

        return html;
    } catch (error) {
        console.warn('LaTeX render error:', error);
        // Fallback: show raw text
        return `<span class="latex-error">${escapeHtml(input)}</span>`;
    }
}

/**
 * Render LaTeX to a DOM element
 * @param {HTMLElement} element 
 * @param {string} input 
 * @param {boolean} displayMode 
 */
export function renderLatexToElement(element, input, displayMode = false) {
    element.innerHTML = renderLatex(input, displayMode);
}

/**
 * Clear the render cache
 */
export function clearRenderCache() {
    renderCache.clear();
}

/**
 * Escape HTML special characters
 * @param {string} text 
 * @returns {string}
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
